// Serviço para busca de imóveis
const RAW_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 
  (process.env.REACT_APP_BACKEND_URL || 'http://localhost:3005');

function normalizeBaseUrl(raw: string): string {
  if (!raw) return 'http://localhost:3005';
  let url = String(raw).trim();
  if (url.startsWith('http//')) url = 'http://' + url.slice(6);
  if (url.startsWith('https//')) url = 'https://' + url.slice(7);
  if (!/^https?:\/\//i.test(url)) {
    url = url.startsWith('//') ? 'http:' + url : 'http://' + url;
  }
  return url.replace(/\/+$/, '');
}

const API_BASE_URL = normalizeBaseUrl(RAW_API_BASE_URL);

export interface SearchFilters {
  cidade?: string;
  estado?: string;
  bairro?: string;
  tipo?: string;
  quartos?: number;
  banheiros?: number;
  vagas_garagem?: number;
  area_min?: number;
  area_max?: number;
  valor_min?: number;
  valor_max?: number;
  aceita_pet?: boolean;
  mobiliado?: boolean;
  ar_condicionado?: boolean;
  internet?: boolean;
  comodidades?: string[];
  search?: string;
  page?: number;
  limit?: number;
}

export interface Property {
  id: string;
  codigo: number;
  titulo: string;
  descricao: string;
  tipo_imovel: string;
  endereco: string;
  cidade: string;
  estado: string;
  cep: string;
  bairro?: string;
  quartos: number;
  banheiros: number;
  vagas_garagem: number;
  area_util: number;
  valor_aluguel: number | null;
  valor_venda?: number | null;
  valor_condominio?: number;
  valor_iptu?: number;
  valor_total: number;
  aceita_pet: boolean;
  mobiliado: boolean;
  ar_condicionado: boolean;
  internet: boolean;
  modalidade_aluguel?: string;
  comodidades: string[];
  status: string;
  midias: Array<{
    id: string;
    url: string;
    tipo: string;
    titulo?: string;
    ordem: number;
  }>;
  locador: {
    id: string;
    nome: string;
    email: string;
    telefone?: string;
  };
  created_at: string;
  updated_at: string;
}

export interface SearchResponse {
  success: boolean;
  data: {
    imoveis: Property[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
  message?: string;
}

class PropertyService {
  private async makeRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const defaultHeaders = {
      'Content-Type': 'application/json',
    };

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Erro ${response.status}: ${response.statusText}`);
      }

      return data;
    } catch (error) {
      console.error('Erro na requisição:', error);
      throw error;
    }
  }

  async searchProperties(filters: SearchFilters = {}): Promise<SearchResponse> {
    // Construir query parameters
    const queryParams = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          // Para arrays (como comodidades), adicionar cada item
          value.forEach(item => queryParams.append(key, item.toString()));
        } else {
          queryParams.append(key, value.toString());
        }
      }
    });

    const queryString = queryParams.toString();
    const endpoint = queryString ? `/api/imoveis?${queryString}` : '/api/imoveis';

    return this.makeRequest(endpoint);
  }

  async getPropertyById(id: string): Promise<{ success: boolean; data: Property }> {
    return this.makeRequest(`/api/imoveis/${id}`);
  }

  async getMyProperties(token: string): Promise<{ success: boolean; data: { imoveis: Property[] } }> {
    return this.makeRequest('/api/imoveis/meus', {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  }
}

export const propertyService = new PropertyService();
